import { Box, Stack, Typography } from '@mui/material';
import { baseTheme } from 'design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { MdFileUpload, MdOutlinePictureAsPdf } from 'react-icons/md';
import useDebouncedValue from 'hooks/useDebouncedValue';
import useFileCompression from 'hooks/useFileCompression';
import { SnackbarVariant, useSnackbar } from 'hooks/useSnackBar';
import { ACCEPTED_FILE_TYPES, isAcceptedUpload, rejectedUploadMessage } from 'utils/acceptedUpload';
import { sanitizeFileType } from 'utils/sanitizeFileType';
import ImageUploadDialog from './ImageUploadDialog';
import ImageUploadFieldType from './ImageUploadFieldType';
import ImageUploadLoader from './ImageUploadLoader';

const DEBOUNCE_TIME = 700;
const IMAGE_SIZE_BUTTON = '30px';
const IMAGE_SIZE_DEFAULT = '64px';

export enum ImageUploadFieldVariant {
  Default = 'Default',
  Button = 'Button',
}

export interface FileMetaData {
  name?: string;
  size?: string;
}

export interface ImageUploadFieldProps {
  variant?: ImageUploadFieldVariant;
  label?: string;
  hasWhiteFields?: boolean;
  files?: string[];
  relatedField?: string;
  onChange: (urls: string[]) => void;
  id: string;
  iconPosition?: 'start' | 'end';
  disabled?: boolean;
  uploadFolder?: string;
  acceptedFileTypes?: string;
  justifyContent?: string;
}

export const extractPathFromUrl = (url: string): string | null => {
  const regex = /\/o\/([^?]+)\?/;
  const match = url.match(regex);

  if (match?.[1]) {
    return decodeURIComponent(match[1]).replace(/%2F/g, '/');
  }

  return null;
};

const isPDFFile = (url: string): boolean => {
  const fileExtension = extractPathFromUrl(url)?.split('.').pop()?.toLowerCase();
  return fileExtension === 'pdf';
};

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  variant = ImageUploadFieldVariant.Button, // TODO: Change to ImageUploadFieldVariant.Default once BE sends field.variant
  label = '',
  hasWhiteFields = false,
  files = [],
  relatedField,
  onChange,
  id,
  justifyContent = 'space-between',
  iconPosition = 'end',
  disabled = false,
  uploadFolder,
  acceptedFileTypes = ACCEPTED_FILE_TYPES,
}) => {
  const [open, setOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [isPDFFirst, setIsPDFFirst] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [compressedFile, compressFile] = useFileCompression();
  const debouncedRelatedField = useDebouncedValue(relatedField, DEBOUNCE_TIME);
  const isDefaultVariant = variant === ImageUploadFieldVariant.Default;

  const hasUploadFile = files.length > 0;
  const isUploadDisabled = disabled || loading;

  // Check if first file is PDF
  useEffect(() => {
    if (files[0]) {
      setIsPDFFirst(isPDFFile(files[0]));
    } else {
      setIsPDFFirst(false);
    }
  }, [files]);

  const handleUploadClick = useCallback(() => {
    if (isUploadDisabled || !inputRef.current) {
      return;
    }
    inputRef.current.click();
  }, [isUploadDisabled]);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];

      if (!selectedFile) {
        compressFile(null);
        return;
      }
      if (!isAcceptedUpload(selectedFile, acceptedFileTypes)) {
        showSnackbar({ message: rejectedUploadMessage(acceptedFileTypes) }, SnackbarVariant.ERROR);
        event.target.value = '';
        return;
      }

      try {
        setLoading(true);
        const sanitizedFile = await sanitizeFileType(selectedFile);
        compressFile(sanitizedFile);
      } catch (error) {
        console.error('Error processing file:', error);
        setLoading(false);
        compressFile(null);
      }

      // Reset input value to allow re-uploading the same file
      event.target.value = '';
    },
    [compressFile, showSnackbar, acceptedFileTypes],
  );

  const handleUploadComplete = useCallback(
    (url: string) => {
      setLoading(false);
      onChange([...files, url]);
      compressFile(null);
    },
    [files, onChange, compressFile],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(files.filter((_, srcIndex) => srcIndex !== index));
    },
    [files, onChange],
  );

  const getImageSize = useCallback(() => {
    return isDefaultVariant ? IMAGE_SIZE_DEFAULT : IMAGE_SIZE_BUTTON;
  }, [isDefaultVariant]);

  const imageSize = getImageSize();

  const stackStyles = useMemo(
    () => ({
      paddingX: variant === ImageUploadFieldVariant.Button && hasUploadFile ? 1 : 0,
      width: '100%',
    }),
    [hasUploadFile, variant],
  );

  const imageStyles = useMemo(
    () => ({
      borderRadius: 8,
      backgroundColor: baseTheme.palette.grey[200],
      objectFit: 'cover' as const,
    }),
    [],
  );

  const pdfIconContainerStyles = useMemo(
    () => ({
      width: imageSize,
      height: imageSize,
      borderRadius: 1,
      opacity: 0.3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    [imageSize],
  );

  const renderUploadIcon = () => (
    <Stack
      sx={{
        width: imageSize,
        height: imageSize,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <MdFileUpload color={isDefaultVariant ? baseTheme.palette.grey[600] : undefined} size={16} />
      {isDefaultVariant && (
        <Typography color={baseTheme.palette.grey[600]} variant="body2">
          Upload
        </Typography>
      )}
    </Stack>
  );

  const renderFilePreview = () => {
    if (!hasUploadFile) return null;

    return (
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
        }}
      >
        {isPDFFirst ? (
          <Box sx={pdfIconContainerStyles}>
            <MdOutlinePictureAsPdf size={40} />
          </Box>
        ) : (
          <img
            src={files[0]}
            alt="Upload preview"
            width={imageSize}
            height={imageSize}
            style={imageStyles}
            loading="lazy"
          />
        )}
        {variant === ImageUploadFieldVariant.Button && <FaPlus />}
      </Stack>
    );
  };

  const renderFieldContent = (justifyContent = 'space-between') => {
    if (loading && compressedFile) {
      return (
        <ImageUploadLoader
          small
          rawFile={compressedFile}
          onComplete={handleUploadComplete}
          uploadFolder={uploadFolder}
          label={label}
          files={files}
        />
      );
    }

    return (
      <Stack
        direction="row"
        sx={[
          {
            alignItems: 'center',
            justifyContent: justifyContent,
          },
          ...(Array.isArray(stackStyles) ? stackStyles : [stackStyles]),
        ]}
      >
        {variant === ImageUploadFieldVariant.Button && iconPosition === 'end' && label}
        {hasUploadFile ? renderFilePreview() : renderUploadIcon()}
        {variant === ImageUploadFieldVariant.Button && iconPosition === 'start' && label}
      </Stack>
    );
  };

  return (
    <Box sx={{ margin: 0 }}>
      <input
        accept={acceptedFileTypes}
        type="file"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        ref={inputRef}
        id={id}
        disabled={isUploadDisabled}
        aria-label={`Upload ${label || 'file'}`}
      />

      <ImageUploadFieldType
        variant={variant}
        label={label}
        justifyContent={justifyContent}
        hasWhiteFields={hasWhiteFields}
        isUploadDisabled={isUploadDisabled}
        hasUploadFile={hasUploadFile}
        onClick={hasUploadFile ? handleOpen : handleUploadClick}
        renderFieldContent={renderFieldContent}
      />

      <ImageUploadDialog
        files={files}
        open={open}
        loading={loading}
        compressedFile={compressedFile}
        label={label}
        reference={debouncedRelatedField}
        onRemove={handleRemove}
        onClose={handleClose}
        onFileUpload={handleFileUpload}
        onUploadComplete={handleUploadComplete}
      />
    </Box>
  );
};

export default ImageUploadField;
